import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { upgradeOf } from '../api/client.ts';
import type { PlanSummary, UpgradeRequired } from '../api/types.ts';
import { Icon, type IconName } from '../components/Icon.tsx';
import {
  PlanMark, RoleBadge, atPlanLimit, orderPlans, plansCounted, ownerLabel, planErrorMessage, useOpenPlan,
} from '../components/PlanSwitcher.tsx';
import { UpgradeNotice, upgradePath } from '../components/UpgradePrompt.tsx';
import { canUpgrade } from '../lib/billing.ts';
import { PeopleDialog, sharedWithLabel } from '../components/PlanPeople.tsx';
import { PageHeader } from '../components/Shell.tsx';
import { Field, Modal } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { now } from '../lib/clock.ts';
import { whenLabel } from '../lib/format.ts';
import { useIsDesktop } from '../lib/hooks.ts';
import { usePlanLimit, useStore } from '../state/store.tsx';
import { useToast } from '../state/toast.tsx';
import { useDocumentTitle } from '../lib/documentTitle.ts';

export const PLAN_NAME_MAX = 60;
/** Above this many plans the cap is a number, not a row of pips. */
const PIP_LIMIT = 10;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

export function planNameError(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return t('plans.new.nameRequired');
  if (trimmed.length > PLAN_NAME_MAX) return t('plans.new.nameTooLong', { max: PLAN_NAME_MAX });
  if (CONTROL.test(trimmed)) return t('plans.new.nameInvalid');
  return null;
}

export function PlansScreen() {
  useDocumentTitle(t('nav.plans'));
  const { plans, activePlanId, billing } = useStore();
  const limit = usePlanLimit();
  const desktop = useIsDesktop();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<PlanSummary | null>(null);
  const [deleting, setDeleting] = useState<PlanSummary | null>(null);
  const [people, setPeople] = useState<PlanSummary | null>(null);
  const sharedHeading = useRef<HTMLHeadingElement>(null);
  const capId = useId();
  const { own, shared } = orderPlans(plans);
  const used = plansCounted(plans);
  const full = atPlanLimit(used, limit);
  const lapsed = billing?.lapsed === true;
  const shownLimit = lapsed ? null : limit;
  const capText = shownLimit === null ? t('plans.capNoLimit', { used }) : t('plans.capLabel', { used, max: shownLimit });

  useEffect(() => {
    if (params.get('new') === null) return;
    params.delete('new');
    setParams(params, { replace: true });
    if (!full) setCreating(true);
  }, [params, setParams, full]);

  const cap = (
    <span className={`cap ${full ? 'cap--full' : ''}`} role="img" aria-label={capText}>
      {shownLimit !== null && shownLimit <= PIP_LIMIT && (
        <span className="cap__pips" aria-hidden="true">
          {Array.from({ length: Math.max(shownLimit, used) }, (_, i) => <span key={i} className={`cap__pip ${i < used ? 'cap__pip--on' : ''}`} />)}
        </span>
      )}
      <span aria-hidden="true">
        {shownLimit === null ? <strong>{capText}</strong> : <><strong>{t('plans.cap', { used, max: shownLimit })}</strong> {t('plans.capWord')}</>}
      </span>
    </span>
  );
  const newButton = (
    <button
      type="button"
      className="button button--primary button--glow plans__new"
      disabled={full}
      {...(full ? { 'aria-describedby': capId } : {})}
      onClick={() => setCreating(true)}
    >
      <Icon name="plus" size={17} strokeWidth={2.4} />{t('plans.new')}
    </button>
  );

  return (
    <div className={desktop ? 'page plans' : 'page page--mobile plans'}>
      {desktop ? (
        <PageHeader title={t('plans.title')} subtitle={t('plans.subtitle')} actions={<>{cap}{newButton}</>} />
      ) : (
        <header className="stack-sm">
          <h1 className="mobile-title">{t('plans.title')}</h1>
          <p className="page-subtitle">{t('plans.subtitle')}</p>
          <div className="plans__bar">{cap}{newButton}</div>
        </header>
      )}
      {full && billing && canUpgrade(billing) ? (
        <div id={capId}><UpgradeNotice upgrade={{ feature: lapsed ? 'lapsed' : 'plans', limit, tier: billing.tier, upgradeUrl: '' }} /></div>
      ) : full && <p className="disabled-reason plans__reason" id={capId}><Icon name="lock" size={13} />{t('plans.capFull')}</p>}
      <div className="plans__layout">
        <div className="plans__groups">
          <section className="plans-group" aria-labelledby={`${capId}-yours`}>
            <div className="plans-group__head">
              <h2 className="eyebrow" id={`${capId}-yours`}>{t('plans.yours')}</h2>
              <span className="fine-print">{capText}</span>
            </div>
            <ul className="plans-list">
              {own.map(({ plan, child }) => (
                <PlanRow
                  key={plan.id}
                  plan={plan}
                  child={child}
                  current={plan.id === activePlanId}
                  parentName={plans.find((p) => p.id === plan.parentPlanId)?.name ?? null}
                  onRename={() => setRenaming(plan)}
                  onDelete={() => setDeleting(plan)}
                  onPeople={() => setPeople(plan)}
                />
              ))}
            </ul>
          </section>
          <section className="plans-group" aria-labelledby={`${capId}-shared`}>
            <div className="plans-group__head">
              <h2 className="eyebrow" id={`${capId}-shared`} ref={sharedHeading} tabIndex={-1}>{t('plans.sharedWithYou')}</h2>
              <span className="fine-print">{t('plans.notCounted')}</span>
            </div>
            {shared.length > 0 ? (
              <ul className="plans-list">
                {shared.map((plan) => <PlanRow key={plan.id} plan={plan} current={plan.id === activePlanId} parentName={null} onPeople={() => setPeople(plan)} />)}
              </ul>
            ) : (
              <div className="plans-empty">
                <span className="plans-empty__title">{t('plans.sharedEmpty.title')}</span>
                {t('plans.sharedEmpty.body')}
              </div>
            )}
          </section>
        </div>
        <HowPlansWork />
      </div>
      {creating && <NewPlanDialog onClose={() => setCreating(false)} />}
      {renaming && <RenameDialog plan={renaming} onClose={() => setRenaming(null)} />}
      {deleting && <DeleteDialog plan={deleting} onClose={() => setDeleting(null)} />}
      {people && <PeopleDialog plan={people} onClose={() => setPeople(null)} onLeft={() => requestAnimationFrame(() => sharedHeading.current?.focus())} />}
    </div>
  );
}

function PlanRow({ plan, child = false, current, parentName, onRename, onDelete, onPeople }: {
  plan: PlanSummary;
  child?: boolean;
  current: boolean;
  parentName: string | null;
  onRename?: () => void;
  onDelete?: () => void;
  onPeople?: () => void;
}) {
  const openPlan = useOpenPlan();
  const owner = plan.role === 'owner';
  const lineage: { icon: IconName; text: string } | null = plan.role !== 'owner'
    ? { icon: 'people', text: t('plans.ownedBy', { name: ownerLabel(plan) }) }
    : plan.parentPlanId
      ? { icon: 'branch', text: parentName ? t('plans.copiedFrom', { name: parentName }) : t('plans.copiedFromGone') }
      : null;
  return (
    <li className={`plans-item ${child ? 'plans-item--child' : ''} ${current ? 'is-current' : ''}`}>
      <button type="button" className="plans-row" aria-label={t('plans.openLabel', { name: plan.name })} {...(current ? { 'aria-current': 'true' as const } : {})} onClick={() => openPlan(plan)}>
        <PlanMark plan={plan} />
        <span className="plans-row__text">
          <span className="plans-row__top">
            <span className="plans-row__name">{plan.name}</span>
            {plan.isPrimary && owner && <span className="role-badge role-badge--primary">{t('plans.primary')}</span>}
            <RoleBadge role={plan.role} />
            {plan.locked && <span className="role-badge role-badge--locked"><Icon name="lock" size={12} strokeWidth={2.2} />{t('plans.locked')}</span>}
            {current && <span className="plans-row__current">{t('plans.openNow')}</span>}
          </span>
          {lineage && <span className="plans-row__lineage"><Icon name={lineage.icon} size={14} />{lineage.text}</span>}
          {owner && (plan.memberCount ?? 0) > 0 && (
            <span className="plans-row__lineage plans-row__people"><Icon name="people" size={14} />{sharedWithLabel(plan.memberCount!)}</span>
          )}
          <span className="plans-row__meta">
            {plan.version === 0 ? t('plans.empty') : t('plans.updated', { when: whenLabel(plan.updatedAt, now()).toLowerCase() })}
          </span>
        </span>
        <span className="plans-row__chev" aria-hidden="true"><Icon name="chevronRight" size={18} /></span>
      </button>
      {(onPeople || (owner && (onRename || onDelete))) && (
        <span className="plans-row__actions">
          {onPeople && (
            <button type="button" className="icon-button plans-row__action" aria-label={t('people.actionLabel', { name: plan.name })} onClick={onPeople}>
              <Icon name="people" size={15} />
            </button>
          )}
          {owner && onRename && (
            <button type="button" className="icon-button plans-row__action" aria-label={t('plans.renameLabel', { name: plan.name })} onClick={onRename}>
              <Icon name="pen" size={15} />
            </button>
          )}
          {owner && onDelete && !plan.isPrimary && (
            <button type="button" className="icon-button plans-row__action plans-row__action--delete" aria-label={t('plans.deleteLabel', { name: plan.name })} onClick={onDelete}>
              <Icon name="trash" size={15} />
            </button>
          )}
        </span>
      )}
    </li>
  );
}

function HowPlansWork() {
  const id = useId();
  const limit = usePlanLimit();
  const items: { icon: IconName; title: string; body: string }[] = [
    { icon: 'pin', title: t('plans.how.primary.title'), body: t('plans.how.primary.body') },
    { icon: 'branch', title: t('plans.how.copy.title'), body: t('plans.how.copy.body') },
    { icon: 'people', title: t('plans.how.shared.title'), body: limit === null ? t('plans.how.shared.bodyNoLimit') : t('plans.how.shared.body', { max: limit }) },
    { icon: 'link', title: t('plans.how.links.title'), body: t('plans.how.links.body') },
  ];
  return (
    <aside className="card card--pad-lg stack-md plans__how" aria-labelledby={id}>
      <h2 className="section-title" id={id}>{t('plans.how.title')}</h2>
      <ul className="explain">
        {items.map((item) => (
          <li key={item.title} className="explain__item">
            <span className="explain__icon" aria-hidden="true"><Icon name={item.icon} size={16} /></span>
            <span><b>{item.title}</b><span>{item.body}</span></span>
          </li>
        ))}
      </ul>
    </aside>
  );
}

export function NewPlanDialog({ onClose }: { onClose: () => void }) {
  const { plans, activePlanId, createPlan, switchPlan, refreshPlans, refreshBilling, billing } = useStore();
  const navigate = useNavigate();
  const toast = useToast();
  const id = useId();
  const copyable = plans.filter((p) => p.version > 0);
  const initialSource = copyable.find((p) => p.id === activePlanId)?.id ?? copyable[0]?.id ?? '';
  const [mode, setMode] = useState<'blank' | 'copy'>('blank');
  const [source, setSource] = useState(initialSource);
  const [name, setName] = useState(t('plans.new.defaultName'));
  const [edited, setEdited] = useState(false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upgrade, setUpgrade] = useState<UpgradeRequired | null>(null);
  const used = plansCounted(plans);
  const limit = usePlanLimit();
  const full = atPlanLimit(used, limit) && canUpgrade(billing);
  const shownUpgrade = upgrade ?? (full ? { feature: billing!.lapsed ? 'lapsed' as const : 'plans' as const, limit, tier: billing!.tier, upgradeUrl: '' } : null);
  const nameError = touched ? planNameError(name) : null;

  const suggest = (nextMode: 'blank' | 'copy', nextSource: string) => {
    if (edited) return;
    const from = plans.find((p) => p.id === nextSource);
    setName(nextMode === 'copy' && from ? t('plans.new.copyName', { name: from.name }).slice(0, PLAN_NAME_MAX) : t('plans.new.defaultName'));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (planNameError(name)) return;
    setBusy(true);
    setError(null);
    setUpgrade(null);
    try {
      const created = await createPlan({ name: name.trim(), ...(mode === 'copy' && source ? { copyFrom: source } : {}) });
      onClose();
      navigate('/');
      await switchPlan(created.id);
      toast.push({ tone: 'success', message: t('plans.created', { name: created.name }) });
    } catch (err) {
      const needed = upgradeOf(err);
      if (needed) {
        setUpgrade(needed);
        await Promise.all([refreshPlans(), refreshBilling()]);
      } else setError(planErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t('plans.new.title')}
      onClose={onClose}
      describedBy={`${id}-used`}
      footer={
        <>
          <button type="button" className="button button--ghost" onClick={onClose}>{t('common.cancel')}</button>
          {shownUpgrade ? (
            <Link to={upgradePath(shownUpgrade.feature)} className="button button--primary">{shownUpgrade.feature === 'lapsed' ? t('billing.resubscribe') : t('upgrade.cta')}</Link>
          ) : (
            <button type="submit" form={`${id}-form`} className="button button--primary" disabled={busy}>
              {busy ? t('plans.new.creating') : t('plans.new.create')}
            </button>
          )}
        </>
      }
    >
      <p className="modal__meta" id={`${id}-used`}>{limit === null ? t('plans.capNoLimit', { used }) : t('plans.new.used', { used, max: limit })}</p>
      <form id={`${id}-form`} className="stack-md" onSubmit={(e) => void submit(e)} noValidate>
        <fieldset className="option-cards">
          <legend className="sr-only">{t('plans.new.how')}</legend>
          <label className="option-card">
            <input type="radio" name={`${id}-mode`} value="blank" checked={mode === 'blank'} onChange={() => { setMode('blank'); suggest('blank', source); }} />
            <span className="option-card__text"><b>{t('plans.new.blank')}</b><span>{t('plans.new.blankBody')}</span></span>
          </label>
          <label className={`option-card ${copyable.length === 0 ? 'option-card--disabled' : ''}`}>
            <input type="radio" name={`${id}-mode`} value="copy" checked={mode === 'copy'} disabled={copyable.length === 0} onChange={() => { setMode('copy'); suggest('copy', source); }} />
            <span className="option-card__text"><b>{t('plans.new.copy')}</b><span>{t('plans.new.copyBody')}</span></span>
          </label>
        </fieldset>
        {mode === 'copy' && (
          <Field label={t('plans.new.from')} htmlFor={`${id}-from`}>
            <select id={`${id}-from`} className="input select" value={source} onChange={(e) => { setSource(e.target.value); suggest('copy', e.target.value); }}>
              {copyable.map((p) => (
                <option key={p.id} value={p.id}>{p.role === 'owner' ? p.name : t('plans.new.fromShared', { name: p.name, owner: ownerLabel(p) })}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label={t('plans.new.name')} htmlFor={`${id}-name`} hint={t('plans.new.nameHint', { max: PLAN_NAME_MAX })} error={nameError}>
          <input
            id={`${id}-name`}
            className="input"
            value={name}
            maxLength={PLAN_NAME_MAX + 20}
            autoComplete="off"
            aria-invalid={nameError ? true : undefined}
            onChange={(e) => { setName(e.target.value); setEdited(true); }}
            onBlur={() => setTouched(true)}
          />
        </Field>
        {error && <p className="form-error" role="alert"><Icon name="alert" size={15} />{error}</p>}
        {shownUpgrade && <UpgradeNotice upgrade={shownUpgrade} hideCta />}
      </form>
    </Modal>
  );
}

function RenameDialog({ plan, onClose }: { plan: PlanSummary; onClose: () => void }) {
  const { renamePlan } = useStore();
  const toast = useToast();
  const id = useId();
  const [name, setName] = useState(plan.name);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameError = touched ? planNameError(name) : null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (planNameError(name)) return;
    setBusy(true);
    try {
      await renamePlan(plan.id, name.trim());
      toast.push({ tone: 'success', message: t('plans.renamed', { name: name.trim() }) });
      onClose();
    } catch (err) {
      setError(planErrorMessage(err));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={t('plans.rename.title')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button button--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" form={`${id}-form`} className="button button--primary" disabled={busy}>{t('plans.rename.save')}</button>
        </>
      }
    >
      <form id={`${id}-form`} className="stack-md" onSubmit={(e) => void submit(e)} noValidate>
        <Field label={t('plans.new.name')} htmlFor={`${id}-name`} hint={t('plans.new.nameHint', { max: PLAN_NAME_MAX })} error={nameError}>
          <input
            id={`${id}-name`}
            className="input"
            value={name}
            autoComplete="off"
            aria-invalid={nameError ? true : undefined}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </Field>
        {error && <p className="form-error" role="alert"><Icon name="alert" size={15} />{error}</p>}
      </form>
    </Modal>
  );
}

function DeleteDialog({ plan, onClose }: { plan: PlanSummary; onClose: () => void }) {
  const { deletePlan } = useStore();
  const toast = useToast();
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    try {
      await deletePlan(plan.id);
      toast.push({ tone: 'success', message: t('plans.deleted', { name: plan.name }) });
      onClose();
    } catch (err) {
      setError(planErrorMessage(err));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={t('plans.delete.title', { name: plan.name })}
      onClose={onClose}
      describedBy={`${id}-body`}
      footer={
        <>
          <button type="button" className="button button--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="button button--warn" disabled={busy} onClick={() => void confirm()}>{t('plans.delete.confirm')}</button>
        </>
      }
    >
      <div className="warn-note" id={`${id}-body`}>
        <Icon name="alert" size={18} />
        <p>{t('plans.delete.body')}</p>
      </div>
      {error && <p className="form-error" role="alert"><Icon name="alert" size={15} />{error}</p>}
    </Modal>
  );
}
